import { useState, useCallback, useRef, useEffect } from "react";

export interface CollectedUserData {
  email?: string;
  username?: string;
  phone?: string;
  [key: string]: any; // Allow additional fields
}

interface GPT4ExtractionResult {
  email?: string | null;
  username?: string | null;
  phone?: string | null;
  confidence?: "high" | "medium" | "low";
}

// Helper function to extract data using GPT-4 API
async function extractDataWithGPT4(
  transcript: string,
  role: "user" | "assistant",
  existingData: CollectedUserData = {}
): Promise<Partial<CollectedUserData>> {
  try {
    console.log("🤖 Using GPT-4 to extract data from transcript:", transcript.substring(0, 100));
    
    const response = await fetch("/api/extract-data", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        transcript,
        role,
        existingData,
      }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: "Unknown error" }));
      console.error("❌ GPT-4 extraction API error:", error);
      return {}; // Return empty object on error, fall back to regex
    }

    const result = await response.json();
    
    if (result.success && result.data) {
      const extracted: Partial<CollectedUserData> = {};
      
      // Check if this is a correction - expanded to handle many different speech patterns
      // Users say corrections in many ways: "no", "that's wrong", "change it", "actually", "my name is X", etc.
      const isCorrection = /(?:no[,.]?\s|that's\s+(?:wrong|not\s+right|incorrect|not\s+correct)|change|correct|update|actually[,.]?\s|it's\s+|it\s+is\s+|can\s+you\s+change|should\s+be|I\s+meant|not\s+right|wrong[,.]?\s|my\s+(?:name|email|phone)\s+is|the\s+(?:name|email|phone)\s+is|I\s+said|let\s+me\s+(?:correct|change)|I\s+want\s+to\s+(?:change|correct)|please\s+(?:change|update|correct))/i.test(transcript);
      
      // ALWAYS extract data if GPT-4 returns it with medium/high confidence
      // This ensures we capture data regardless of how the user phrases it
      if (result.data.email && (result.data.confidence === "high" || result.data.confidence === "medium" || !existingData.email || isCorrection)) {
        extracted.email = result.data.email;
        console.log("✅ Extracting email (correction:", isCorrection, "confidence:", result.data.confidence, "):", result.data.email);
      }
      
      if (result.data.username && (result.data.confidence === "high" || result.data.confidence === "medium" || !existingData.username || isCorrection)) {
        extracted.username = result.data.username;
      }
      
      if (result.data.phone && (result.data.confidence === "high" || result.data.confidence === "medium" || !existingData.phone || isCorrection)) {
        extracted.phone = result.data.phone;
      }
      
      console.log("✅ GPT-4 extracted data:", extracted);
      return extracted;
    }
    
    return {}; // Return empty on no data
  } catch (error) {
    console.error("❌ Error calling GPT-4 extraction API:", error);
    return {}; // Return empty on error, fall back to regex
  }
}

interface UseVapiDataCollectionReturn {
  collectedData: CollectedUserData;
  isDataComplete: boolean;
  sendDataToTeam: () => Promise<void>;
  isSending: boolean;
  sendError: string | null;
  resetData: () => void;
  handleVapiData: (event: any) => void;
  showConfirmation: boolean;
  setShowConfirmation: (show: boolean) => void;
  resetConfirmationDetection: () => void;
  updateCollectedData: (newData: CollectedUserData) => void;
}

// Helper function to extract email from various formats
function extractEmail(text: string): string | null {
  console.log("🔍 Extracting email from text:", text);
  
  // First try standard email regex - but exclude common false positives
  const emailRegex = /\b([a-zA-Z0-9](?:[a-zA-Z0-9._-]*[a-zA-Z0-9])?@[a-zA-Z0-9](?:[a-zA-Z0-9.-]*[a-zA-Z0-9])?\.[a-zA-Z]{2,})\b/g;
  let matches = text.match(emailRegex);
  
  if (matches && matches.length > 0) {
    // Filter out obvious false positives
    const validEmails = matches.filter(email => {
      const lower = email.toLowerCase();
      // Exclude common false positives
      if (lower.includes('example.com') || lower.includes('test.com') || lower.includes('sample.com')) return false;
      // Must have a valid domain (at least 2 chars after dot)
      if (!/\.\w{2,}$/.test(lower)) return false;
      // Local part must be reasonable (not just a single char or too long)
      const [localPart] = lower.split('@');
      if (!localPart || localPart.length < 2 || localPart.length > 64) return false;
      return true;
    });
    
    if (validEmails.length > 0) {
      // Prefer the first valid email, or the most complete one
      const bestEmail = validEmails[0].toLowerCase().trim();
      console.log("✅ Found standard email format:", bestEmail);
      return bestEmail;
    }
  }
  
  // Also try to extract from correction patterns like "no, my email is X" or "change email to X"
  const correctionPattern = /(?:no|change|correct|update|actually|it's|can\s+you\s+change).*?(?:email|e-mail)\s+(?:is|to|as)?\s*([a-zA-Z0-9](?:[a-zA-Z0-9._-]*[a-zA-Z0-9])?@[a-zA-Z0-9](?:[a-zA-Z0-9.-]*[a-zA-Z0-9])?\.[a-zA-Z]{2,})/i;
  const correctionMatch = text.match(correctionPattern);
  if (correctionMatch && correctionMatch[1]) {
    const correctedEmail = correctionMatch[1].toLowerCase().trim();
    if (isValidEmail(correctedEmail)) {
      console.log("✅ Found email from correction pattern:", correctedEmail);
      return correctedEmail;
    }
  }
  
  // Try to extract from spoken format like "SalaiTeam dot Sigree1 at gmail dot com"
  // Pattern 1: Handle "word(s) dot word at word dot word" - most common format
  // This pattern handles spaces in names like "Salai Team dot Sigree1"
  const spokenEmailPattern = /([a-zA-Z0-9]+(?:\s+[a-zA-Z0-9]+)*(?:\s+(?:dot|\.)\s+[a-zA-Z0-9]+)+)\s+(?:at|@)\s+([a-zA-Z]+(?:\s+(?:dot|\.)\s+[a-zA-Z]+)+)/i;
  let match = text.match(spokenEmailPattern);
  
  if (match) {
    let localPart = match[1]
      .replace(/\s+(?:dot|\.)\s+/gi, ".")
      .replace(/\s+/g, "")
      .toLowerCase();
    let domainPart = match[2]
      .replace(/\s+(?:dot|\.)\s+/gi, ".")
      .replace(/\s+/g, "")
      .toLowerCase();
    const email = `${localPart}@${domainPart}`;
    console.log("🔍 Attempting pattern 1 (with dot):", { localPart, domainPart, email });
    if (isValidEmail(email)) {
      console.log("✅ Found valid email (pattern 1):", email);
      return email;
    }
  }
  
  // Pattern 2: Handle "It's james bond at Gmail dot com" format (spaces between words)
  // CRITICAL: The local part should have NO DOT - "james bond" becomes "jamesbond" not "james.bond"
  // The domain part: "gmail dot com" or "g mail dot com" becomes "gmail.com"
  const itsPattern = /(?:it's|it\s+is)\s+([a-zA-Z]+(?:\s+[a-zA-Z]+)*)\s+(?:at|@)\s+([a-zA-Z]+(?:\s+[a-zA-Z]+)*)\s+(?:dot|\.)\s+([a-zA-Z]+)/i;
  match = text.match(itsPattern);
  if (match && match[1] && match[2] && match[3]) {
    // IMPORTANT: Convert "james bond" to "jamesbond" - NO DOT between name parts
    let localPart = match[1].replace(/\s+/g, "").toLowerCase();
    // Convert "g mail" to "gmail" - remove spaces
    let domainName = match[2].replace(/\s+/g, "").toLowerCase();
    // TLD is captured separately (e.g., "com")
    let tld = match[3].toLowerCase();
    const email = `${localPart}@${domainName}.${tld}`;
    console.log("🔍 Attempting pattern 2 (It's X at Y dot Z format):", { localPart, domainName, tld, email });
    if (isValidEmail(email)) {
      console.log("✅ Found valid email (pattern 2 - It's format):", email);
      return email;
    }
  }
  
  // Pattern 2 fallback: Handle partial transcripts where TLD might be missing
  // "it's james bond at gmail" -> wait for more data
  const itsPartialPattern = /(?:it's|it\s+is)\s+([a-zA-Z]+(?:\s+[a-zA-Z]+)*)\s+(?:at|@)\s+([a-zA-Z]+(?:\s+[a-zA-Z]+)*)\s*$/i;
  match = text.match(itsPartialPattern);
  if (match && match[1] && match[2]) {
    let localPart = match[1].replace(/\s+/g, "").toLowerCase();
    let domainPart = match[2].replace(/\s+/g, "").toLowerCase();
    // Only add .com if it looks like a known domain
    if (domainPart === "gmail" || domainPart === "yahoo" || domainPart === "outlook" || domainPart === "hotmail") {
      const email = `${localPart}@${domainPart}.com`;
      console.log("🔍 Attempting pattern 2 fallback (partial):", { localPart, domainPart, email });
      if (isValidEmail(email)) {
        console.log("✅ Found valid email (pattern 2 fallback):", email);
        return email;
      }
    }
  }
  
  // Pattern 2b: More flexible - extract anything before "at" and after "at"
  // BUT: Skip if it's a conversational phrase like "your email is..." (that's just the assistant talking)
  // Only extract if it looks like user is actually providing their email
  const isConversational = /\b(your\s+email\s+is|email\s+is|your\s+email|the\s+email\s+is)\b/i.test(text);
  if (!isConversational) {
    const atPattern = /([a-zA-Z0-9]+(?:\s+[a-zA-Z0-9]+)*(?:\s+(?:dot|\.)\s+[a-zA-Z0-9]+)*)\s+(?:at|@)\s+([a-zA-Z]+(?:\s+(?:dot|\.)\s+[a-zA-Z]+)+)/i;
    match = text.match(atPattern);
    
    if (match) {
      let localPart = match[1]
        .replace(/\s+(?:dot|\.)\s+/gi, ".")
        .replace(/\s+/g, "")
        .toLowerCase();
      let domainPart = match[2]
        .replace(/\s+(?:dot|\.)\s+/gi, ".")
        .replace(/\s+/g, "")
        .toLowerCase();
      
      // Validate parts are reasonable - don't accept if local part is too long (might be conversation)
      if (localPart.length >= 2 && localPart.length <= 64 && domainPart.length >= 4) {
        // Reject if local part contains common words that suggest it's conversational text
        const suspiciousLocalParts = /(your|email|is|as|the|this|that|what|how|when|where|why|you|can|could|would|should)/i;
        if (!suspiciousLocalParts.test(localPart)) {
          // If domain doesn't have a dot, add .com (common case)
          if (!domainPart.includes(".") && domainPart.length > 0) {
            domainPart = `${domainPart}.com`;
          }
          
          const email = `${localPart}@${domainPart}`;
          console.log("🔍 Attempting pattern 2b (flexible at):", { localPart, domainPart, email });
          if (isValidEmail(email)) {
            console.log("✅ Found valid email (pattern 2b):", email);
            return email;
          }
        } else {
          console.log("⚠️ Skipping suspicious email local part (likely conversation):", localPart);
        }
      }
    }
  } else {
    console.log("⚠️ Skipping email extraction - appears to be conversational phrase");
  }
  
  // Pattern 3: Handle "email as X at Y" format (from assistant confirmations only)
  // But be careful - don't extract from conversational phrases like "your email is..."
  const emailAsPattern = /(?:^|\.|,)\s*(?:your\s+)?email\s+(?:is|as)\s+([a-zA-Z0-9]+(?:\s+[a-zA-Z0-9]+)*(?:\s+(?:dot|\.)\s+[a-zA-Z0-9]+)*)\s+(?:at|@)\s+([a-zA-Z]+(?:\s+(?:dot|\.)\s+[a-zA-Z]+)+)/i;
  match = text.match(emailAsPattern);
  
  if (match) {
    let localPart = match[1]
      .replace(/\s+(?:dot|\.)\s+/gi, ".")
      .replace(/\s+/g, "")
      .toLowerCase();
    let domainPart = match[2]
      .replace(/\s+(?:dot|\.)\s+/gi, ".")
      .replace(/\s+/g, "")
      .toLowerCase();
    
    // Validate the parts are reasonable
    if (localPart.length >= 2 && localPart.length <= 64 && domainPart.length >= 4) {
      if (!domainPart.includes(".") && domainPart.length > 0) {
        domainPart = `${domainPart}.com`;
      }
      
      const email = `${localPart}@${domainPart}`;
      console.log("🔍 Attempting pattern 3 (email as):", { localPart, domainPart, email });
      if (isValidEmail(email)) {
        console.log("✅ Found valid email (pattern 3):", email);
        return email;
      }
    }
  }
  
  // Pattern 4: Handle correction patterns like "no, my email is X" or "change email to X"
  const correctionEmailPatterns = [
    /(?:no|change|correct|update|actually|it's|can\s+you\s+change).*?(?:my\s+)?email\s+(?:is|to|as)?\s*([a-zA-Z0-9](?:[a-zA-Z0-9._-]*[a-zA-Z0-9])?@[a-zA-Z0-9](?:[a-zA-Z0-9.-]*[a-zA-Z0-9])?\.[a-zA-Z]{2,})/i,
    /(?:no|change|correct|update|actually|it's|can\s+you\s+change).*?(?:my\s+)?email\s+(?:is|to|as)?\s+([a-zA-Z0-9]+(?:\s+[a-zA-Z0-9]+)*(?:\s+(?:dot|\.)\s+[a-zA-Z0-9]+)*)\s+(?:at|@)\s+([a-zA-Z]+(?:\s+(?:dot|\.)\s+[a-zA-Z]+)+)/i,
  ];
  
  for (const pattern of correctionEmailPatterns) {
    match = text.match(pattern);
    if (match) {
      let email: string;
      if (match[1] && match[1].includes('@')) {
        // Standard email format
        email = match[1].toLowerCase().trim();
      } else if (match[1] && match[2]) {
        // Spoken format
        let localPart = match[1]
          .replace(/\s+(?:dot|\.)\s+/gi, ".")
          .replace(/\s+/g, "")
          .toLowerCase();
        let domainPart = match[2]
          .replace(/\s+(?:dot|\.)\s+/gi, ".")
          .replace(/\s+/g, "")
          .toLowerCase();
        if (!domainPart.includes(".") && domainPart.length > 0) {
          domainPart = `${domainPart}.com`;
        }
        email = `${localPart}@${domainPart}`;
      } else {
        continue;
      }
      
      if (isValidEmail(email)) {
        console.log("✅ Found email from correction pattern:", email);
        return email;
      }
    }
  }
  
  console.log("❌ No email found in text");
  return null;
}

// Helper function to extract phone from various formats
function extractPhone(text: string): string | null {
  console.log("🔍 Extracting phone from text:", text);
  
  // First try standard phone regex (handles formats like 418-575-5177, (418) 575-5177, etc.)
  // Improved regex to catch more formats
  const phoneRegex = /(?:^|[^\d])([\+]?[(]?[0-9]{1,4}[)]?[-\s\.]?[(]?[0-9]{1,4}[)]?[-\s\.]?[0-9]{1,9})(?:[^\d]|$)/g;
  let matches = Array.from(text.matchAll(phoneRegex));
  
  if (matches.length > 0) {
    // Get the match with exactly 10 or 11 digits when cleaned, or closest to 10
    const validPhones = matches
      .map(m => m[1].trim())
      .filter(phone => {
        const cleaned = phone.replace(/[\s\.\(\)\-]/g, "");
        return cleaned.length >= 10 && cleaned.length <= 15; // 10-15 digits is valid
      });
    
    if (validPhones.length > 0) {
      // Prefer 10-digit numbers
      const bestPhone = validPhones.sort((a, b) => {
        const aClean = a.replace(/[\s\.\(\)\-]/g, "");
        const bClean = b.replace(/[\s\.\(\)\-]/g, "");
        // Prefer exactly 10 digits
        if (aClean.length === 10 && bClean.length !== 10) return -1;
        if (bClean.length === 10 && aClean.length !== 10) return 1;
        return Math.abs(10 - aClean.length) - Math.abs(10 - bClean.length);
      })[0];
      
      const cleaned = bestPhone.replace(/[\s\.\(\)]/g, "");
      if (cleaned.length === 10) {
        const formatted = `${cleaned.slice(0, 3)}-${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
        console.log("✅ Found standard phone format:", formatted);
        return formatted;
      } else if (cleaned.length >= 10) {
        // Keep original format for international numbers
        const formatted = cleaned.length === 11 && cleaned.startsWith('1') 
          ? `${cleaned.slice(1, 4)}-${cleaned.slice(4, 7)}-${cleaned.slice(7)}`
          : bestPhone.replace(/[\s]/g, "");
        console.log("✅ Found standard phone format:", formatted);
        return formatted;
      }
    }
  }
  
  // Try to extract from format like "4 1 8 5 7 5 5 1 7 7" (spaced digits)
  // Handle patterns like "my phone is", "phone number is", "your phone as", "phone as"
  const spacedDigitsPattern = /(?:my\s+)?(?:phone\s+number|number|phone)\s+(?:is|as|to|your\s+phone\s+as|phone\s+as)?\s*((?:\d\s*){10,})/i;
  let match = text.match(spacedDigitsPattern);
  
  if (match) {
    let phoneStr = match[1].replace(/\s+/g, "");
    if (phoneStr.length === 10) {
      const formatted = `${phoneStr.slice(0, 3)}-${phoneStr.slice(3, 6)}-${phoneStr.slice(6)}`;
      console.log("✅ Found spaced digits phone format:", formatted);
      return formatted;
    } else if (phoneStr.length >= 10) {
      console.log("✅ Found spaced digits phone format:", phoneStr);
      return phoneStr;
    }
  }
  
  // Try to extract from spoken format like "four one eight five seven five five one seven seven"
  // Also handle "your phone number as" (from assistant confirmations)
  const spokenPhonePattern = /(?:my\s+)?(?:phone\s+number|number|phone)\s+(?:is|as|to|your\s+phone\s+as|phone\s+as)?\s+((?:four|one|eight|five|seven|two|three|six|nine|zero|\d)(?:\s+(?:four|one|eight|five|seven|two|three|six|nine|zero|\d|-|\s))*)/i;
  match = text.match(spokenPhonePattern);
  
  if (match) {
    let phoneStr = match[1];
    // Convert spoken numbers to digits
    const numberMap: Record<string, string> = {
      "zero": "0", "one": "1", "two": "2", "three": "3", "four": "4",
      "five": "5", "six": "6", "seven": "7", "eight": "8", "nine": "9"
    };
    
    phoneStr = phoneStr.replace(/\b(zero|one|two|three|four|five|six|seven|eight|nine)\b/gi, (match) => {
      return numberMap[match.toLowerCase()] || match;
    });
    
    // Remove extra spaces and keep only digits and dashes
    phoneStr = phoneStr.replace(/\s+/g, "").replace(/[^\d-]/g, "");
    
    // Format as XXX-XXX-XXXX if we have 10 digits
    if (phoneStr.length === 10) {
      const formatted = `${phoneStr.slice(0, 3)}-${phoneStr.slice(3, 6)}-${phoneStr.slice(6)}`;
      console.log("✅ Found spoken phone format:", formatted);
      return formatted;
    } else if (phoneStr.length >= 10) {
      console.log("✅ Found spoken phone format:", phoneStr);
      return phoneStr;
    }
  }
  
  // Try to find phone in confirmation patterns like "your phone number as 4-1-8..." or "phone as 4 1 8..."
  const confirmationPhonePattern = /(?:your\s+phone\s+(?:number\s+)?as|phone\s+(?:number\s+)?as)\s+((?:[\d\s\-]{10,}|(?:four|one|eight|five|seven|two|three|six|nine|zero)(?:\s+(?:four|one|eight|five|seven|two|three|six|nine|zero|\d|\s|-))*))/i;
  match = text.match(confirmationPhonePattern);
  
  if (match) {
    let phoneStr = match[1];
    // Convert spoken numbers if present
    const numberMap: Record<string, string> = {
      "zero": "0", "one": "1", "two": "2", "three": "3", "four": "4",
      "five": "5", "six": "6", "seven": "7", "eight": "8", "nine": "9"
    };
    phoneStr = phoneStr.replace(/\b(zero|one|two|three|four|five|six|seven|eight|nine)\b/gi, (match) => {
      return numberMap[match.toLowerCase()] || match;
    });
    phoneStr = phoneStr.replace(/\s+/g, "").replace(/[^\d-]/g, "");
    
    if (phoneStr.length === 10) {
      const formatted = `${phoneStr.slice(0, 3)}-${phoneStr.slice(3, 6)}-${phoneStr.slice(6)}`;
      console.log("✅ Found confirmation phone format:", formatted);
      return formatted;
    } else if (phoneStr.length >= 10) {
      console.log("✅ Found confirmation phone format:", phoneStr);
      return phoneStr;
    }
  }
  
  // Last resort: find any sequence of 10+ digits in the text
  const anyDigitsPattern = /(\d[\s\-]?){10,}/g;
  matches = Array.from(text.matchAll(anyDigitsPattern));
  
  if (matches.length > 0) {
    // Get the longest match that's close to 10 digits
    const bestMatch = matches
      .map(m => m[0])
      .sort((a, b) => {
        const aClean = a.replace(/[\s\-]/g, "");
        const bClean = b.replace(/[\s\-]/g, "");
        // Prefer exactly 10 digits
        if (aClean.length === 10 && bClean.length !== 10) return -1;
        if (bClean.length === 10 && aClean.length !== 10) return 1;
        return Math.abs(10 - aClean.length) - Math.abs(10 - bClean.length);
      })[0];
    
    let phoneStr = bestMatch.replace(/[\s\-]/g, "");
    if (phoneStr.length === 10) {
      const formatted = `${phoneStr.slice(0, 3)}-${phoneStr.slice(3, 6)}-${phoneStr.slice(6)}`;
      console.log("✅ Found any digits phone format:", formatted);
      return formatted;
    } else if (phoneStr.length >= 10 && phoneStr.length <= 15) {
      console.log("✅ Found any digits phone format:", phoneStr);
      return phoneStr;
    }
  }
  
  console.log("❌ No phone found in text");
  return null;
}

// Helper function to validate email
function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

// Helper function to validate phone (basic validation)
function isValidPhone(phone: string): boolean {
  // Remove common formatting characters
  const cleaned = phone.replace(/[\s\-\(\)\.]/g, "");
  // Check if it has at least 10 digits
  return /^\+?[0-9]{10,}$/.test(cleaned);
}

export function useVapiDataCollection(): UseVapiDataCollectionReturn {
  const [collectedData, setCollectedData] = useState<CollectedUserData>({});
  const [isSending, setIsSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const hasSentRef = useRef(false); // Prevent duplicate sends
  const confirmationDetectedRef = useRef(false); // Track if VAPI mentioned confirmation
  
  // Check if text contains confirmation-related phrases
  // Expanded to catch more confirmation patterns
  const checkForConfirmationPhrase = (text: string): boolean => {
    if (!text || typeof text !== "string") return false;
    const lowerText = text.toLowerCase();
    
    // More specific phrases that indicate VAPI is asking for confirmation
    const confirmationPhrases = [
      "let me confirm",
      "let me verify",
      "just to confirm",
      "to confirm your",
      "to confirm the",
      "i'll confirm",
      "i will confirm",
      "confirming your",
      "confirming the",
      "please confirm",
      "would you like to confirm",
      "shall i confirm",
      "can i confirm",
      "i have your",
      "your email as",
      "your phone as",
      "your name as",
      "email as",
      "phone as",
      "name as",
      "i've got your",
      "got your",
      "so your",
      "so we have",
      "we have your",
    ];
    
    // Must match a phrase AND be in a complete sentence context
    const hasPhrase = confirmationPhrases.some(phrase => lowerText.includes(phrase));
    
    // Additional check: should be in a sentence, not just a random word
    if (hasPhrase) {
      // Make sure it's not just a false positive (like "confirm" in a different context)
      const confirmContext = /(?:let me|just to|to|i'll|i will|please|would you|shall i|can i|i have|your|so|we have|got)\s+(?:confirm|verify|email|phone|name|as|got)/i;
      return confirmContext.test(lowerText);
    }
    
    return false;
  };
  
  // Check if text contains data confirmation patterns (AI confirming back the data)
  const checkForDataConfirmation = (text: string): boolean => {
    if (!text || typeof text !== "string") return false;
    const lowerText = text.toLowerCase();
    
    // Patterns that indicate AI is confirming back the user's data
    const dataConfirmationPatterns = [
      /your\s+email\s+(?:is|as)/i,
      /your\s+phone\s+(?:number\s+)?(?:is|as)/i,
      /your\s+name\s+(?:is|as)/i,
      /i\s+have\s+your\s+(?:email|phone|name)/i,
      /so\s+your\s+(?:email|phone|name)/i,
      /we\s+have\s+your\s+(?:email|phone|name)/i,
      /got\s+your\s+(?:email|phone|name)/i,
    ];
    
    return dataConfirmationPatterns.some(pattern => pattern.test(lowerText));
  };

  // Handler function that will be passed to useVapi hook
  const handleVapiData = useCallback((event: any) => {
    console.log("🔍 Processing VAPI data event:", event);
    console.log("🔍 Event type:", event?.type);
    console.log("🔍 Full event data:", JSON.stringify(event, null, 2));

      let newData: Partial<CollectedUserData> = {};

      // Handle function-call events
      if (event.type === "function-call" && event.parameters) {
        const params = event.parameters;
        
        // Extract email, username, phone from parameters
        if (params.email) newData.email = String(params.email).trim();
        if (params.username || params.name) {
          newData.username = String(params.username || params.name).trim();
        }
        if (params.phone || params.phoneNumber) {
          newData.phone = String(params.phone || params.phoneNumber).trim();
        }

        // Also check for any other fields
        Object.keys(params).forEach((key) => {
          if (!["email", "username", "name", "phone", "phoneNumber"].includes(key)) {
            newData[key] = params[key];
          }
        });
      }

      // Handle function-call-result events
      if (event.type === "function-call-result" && event.result) {
        const result = event.result;
        if (typeof result === "object") {
          if (result.email) newData.email = String(result.email).trim();
          if (result.username || result.name) {
            newData.username = String(result.username || result.name).trim();
          }
          if (result.phone || result.phoneNumber) {
            newData.phone = String(result.phone || result.phoneNumber).trim();
          }
        }
      }

      // Handle message-end events - try to extract data from message text
      // Extract from user messages only (to avoid assistant's examples)
      if (event.type === "message-end" && event.message && event.role === "user") {
        const messageText = typeof event.message === "string" 
          ? event.message 
          : event.message.content || event.message.text || JSON.stringify(event.message);


        // Check if user is correcting/changing information - expanded for many speech patterns
        const isCorrection = /(?:no[,.]?\s|that's\s+(?:wrong|not\s+right|incorrect|not\s+correct)|change|correct|update|actually[,.]?\s|it's\s+|it\s+is\s+|can\s+you\s+change|should\s+be|I\s+meant|not\s+right|wrong[,.]?\s|my\s+(?:name|email|phone)\s+is|the\s+(?:name|email|phone)\s+is|I\s+said|let\s+me\s+(?:correct|change)|I\s+want\s+to\s+(?:change|correct)|please\s+(?:change|update|correct))/i.test(messageText);
        
        // ALWAYS use GPT-4 for user messages to catch all speech patterns
        // Different users speak differently, so we can't rely on simple patterns
        if (messageText.length > 5) {
          extractDataWithGPT4(messageText, "user", collectedData).then((gptData) => {
            if (Object.keys(gptData).length > 0) {
              setCollectedData((prev) => {
                const updated = { ...prev };
                if (gptData.email && isValidEmail(gptData.email)) {
                  console.log("✅ Updated email from correction (GPT-4):", gptData.email);
                  updated.email = gptData.email;
                }
                if (gptData.username && gptData.username.length >= 2) {
                  console.log("✅ Updated username from correction (GPT-4):", gptData.username);
                  updated.username = gptData.username;
                }
                if (gptData.phone && isValidPhone(gptData.phone)) {
                  console.log("✅ Updated phone from correction (GPT-4):", gptData.phone);
                  updated.phone = gptData.phone;
                }
                return updated;
              });
            }
          });
        }

        // Extract email - allow updates (always extract if present)
        const extractedEmail = extractEmail(messageText);
        if (extractedEmail) {
          console.log("✅ Extracted email from message-end:", extractedEmail);
          newData.email = extractedEmail;
        }

        // Extract phone - allow updates
        const extractedPhone = extractPhone(messageText);
        if (extractedPhone) {
          console.log("✅ Extracted phone from message-end:", extractedPhone);
          newData.phone = extractedPhone;
        }
        
        // Extract name
        const namePatterns = [
          /(?:my\s+name\s+is|I'm|I\s+am|name\s+is|call\s+me|it's|it\s+is|this\s+is)\s+([A-Z][a-z]+(?:\s+[A-Z][a-z]+)*)/i,
          /(?:full\s+name\s+is|the\s+name\s+is|name\s+as)\s+([A-Z][a-z]+(?:\s+[A-Z][a-z]+)+)/i,
          /(?:^|\.|\?|!|,)\s*([A-Z][a-z]{2,}(?:\s+[A-Z][a-z]{2,})*)\s*(?:\.|,|$|is|my|email|phone)/i,
        ];
        
        for (const pattern of namePatterns) {
          const match = messageText.match(pattern);
          if (match && match[1]) {
            const name = match[1].trim();
            const invalidWords = [
              "Team", "Email", "Phone", "Number", "Assistant", "AI", "My", "The", "Is", "As",
              "Hi", "Hello", "Hey", "Thanks", "Thank", "Yes", "No", "Okay", "OK", "Sure",
              "Sorry", "Please", "Well", "Um", "Uh", "Like", "You", "Your", "This", "That"
            ];
            
            const nameParts = name.split(/\s+/).filter(part => part.length > 0);
            const validParts = nameParts.filter(part => {
              const trimmed = part.trim();
              if (trimmed.length < 2) return false;
              if (!/^[A-Z][a-z]+$/.test(trimmed)) return false;
              if (invalidWords.includes(trimmed)) return false;
              if (/^(am|is|are|was|were|be|have|has|had|do|does|did|will|would|can|could)$/i.test(trimmed)) return false;
              return true;
            });
            
            if (validParts.length > 0 && validParts.length <= 4) {
              const validName = validParts.join(" ");
              if (validName.length >= 3 && validName.length <= 50) {
                console.log("✅ Extracted name from message-end:", validName);
                newData.username = validName;
                break;
              }
            }
          }
        }
      }

      // Handle transcript events - parse for structured data using GPT-4
      // IMPORTANT: Extract from BOTH user and assistant transcripts for real-time updates
      if (event.type === "transcript" && event.transcript) {
        const transcript = typeof event.transcript === "string"
          ? event.transcript
          : JSON.stringify(event.transcript);
        
        const isUser = event.role === "user";
        const isAssistant = event.role === "assistant";
        const isPartial = event.transcriptType === "partial";
        const isFinal = event.transcriptType === "final";

        console.log(`📝 Processing ${event.role} transcript (${event.transcriptType}) for data extraction:`, transcript.substring(0, 100));
        
        // CRITICAL: Extract from ASSISTANT transcripts in real-time as they confirm data
        // When AI says "to confirm" and starts reading back data, extract it immediately
        if (isAssistant) {
          // Check if assistant is confirming data (starts with confirmation phrases or contains "as" or "it's")
          const isConfirmingData = /(?:let me confirm|just to confirm|to confirm|it's|it is|your (?:email|phone|name) (?:as|is)|email (?:as|is)|phone (?:as|is))/i.test(transcript);
          
          // Extract from BOTH partial and final transcripts for real-time updates
          // Partial transcripts allow instant modal updates as AI speaks
          if (isConfirmingData || isPartial || isFinal) {
            // Extract immediately from transcripts for real-time updates
            // Use regex for instant extraction as AI speaks
            
            // Extract email from confirmation patterns - handle "It's james charles at g mail dot com" format
            // Pattern 1: Standard email format "your email as email@domain.com"
            const emailMatch = transcript.match(/(?:your\s+email\s+(?:as|is)|email\s+(?:as|is)|it's|it\s+is)\s+([a-zA-Z0-9](?:[a-zA-Z0-9._-]*[a-zA-Z0-9])?@[a-zA-Z0-9](?:[a-zA-Z0-9.-]*[a-zA-Z0-9])?\.[a-zA-Z]{2,})/i);
            if (emailMatch && emailMatch[1]) {
              const email = emailMatch[1].toLowerCase().trim();
              if (isValidEmail(email)) {
                console.log(`✅ Extracted email from assistant confirmation (${isPartial ? 'partial' : 'final'}):`, email);
                newData.email = email;
                // Update immediately for real-time modal update
                setCollectedData((prev) => ({ ...prev, email }));
              }
            }
            
            // Pattern 2: Handle spoken email format with "dot" in domain like "james charles at g mail dot com"
            // This pattern captures: localPart "at" domain "dot" tld
            const itsEmailPattern = /(?:it's|it\s+is)\s+([a-zA-Z]+(?:\s+[a-zA-Z]+)*)\s+(?:at|@)\s+([a-zA-Z]+(?:\s+[a-zA-Z]+)*)\s+(?:dot|\.)\s+([a-zA-Z]+)/i;
            const itsEmailMatch = transcript.match(itsEmailPattern);
            if (itsEmailMatch && itsEmailMatch[1] && itsEmailMatch[2] && itsEmailMatch[3]) {
              // IMPORTANT: Convert "james bond" to "jamesbond" - NO DOT between name parts
              let localPart = itsEmailMatch[1].replace(/\s+/g, "").toLowerCase();
              // Convert "g mail" to "gmail" - remove spaces
              let domainName = itsEmailMatch[2].replace(/\s+/g, "").toLowerCase();
              // TLD is captured separately (e.g., "com")
              let tld = itsEmailMatch[3].toLowerCase();
              const email = `${localPart}@${domainName}.${tld}`;
              if (isValidEmail(email)) {
                console.log(`✅ Extracted email from "It's X at Y dot Z" format (${isPartial ? 'partial' : 'final'}):`, email);
                newData.email = email;
                // Update immediately for real-time modal update
                setCollectedData((prev) => ({ ...prev, email }));
              }
            }
            
            // Pattern 2b: Also handle "Let's make sure we have the email correct. It's james charles at g mail dot com"
            // This is the actual format from the logs - check this BEFORE pattern 2 since it's more specific
            if (!newData.email) {
              const emailCorrectPattern = /(?:email\s+correct|have\s+the\s+email|make\s+sure.*?email).*?(?:it's|it\s+is)\s+([a-zA-Z]+(?:\s+[a-zA-Z]+)*)\s+(?:at|@)\s+([a-zA-Z]+(?:\s+[a-zA-Z]+)*)(?:\s+(?:dot|\.)\s+([a-zA-Z]+))?/i;
              const emailCorrectMatch = transcript.match(emailCorrectPattern);
              if (emailCorrectMatch && emailCorrectMatch[1] && emailCorrectMatch[2]) {
                // IMPORTANT: Convert "james bond" to "jamesbond" - NO DOT between name parts
                let localPart = emailCorrectMatch[1].replace(/\s+/g, "").toLowerCase();
                let domainPart = emailCorrectMatch[2].replace(/\s+/g, "").toLowerCase();
                if (emailCorrectMatch[3]) {
                  domainPart = `${domainPart}.${emailCorrectMatch[3].toLowerCase()}`;
                } else {
                  // For partial transcripts or when TLD is missing, default to ".com" if domain looks valid
                  if (domainPart.length >= 2) {
                    domainPart = `${domainPart}.com`;
                  }
                }
                const email = `${localPart}@${domainPart}`;
                if (isValidEmail(email)) {
                  console.log(`✅ Extracted email from "email correct" format (${isPartial ? 'partial' : 'final'}):`, email);
                  newData.email = email;
                  // Update immediately for real-time modal update
                  setCollectedData((prev) => ({ ...prev, email }));
                }
              }
            }
            
            // Pattern 3: Handle "your email as james charles at g mail dot com" or "email as james charles at g mail dot com" format
            // Only check if we haven't extracted email yet
            if (!newData.email) {
              const spokenEmailMatch = transcript.match(/(?:your\s+email\s+(?:as|is)|email\s+(?:as|is))\s+([a-zA-Z0-9]+(?:\s+[a-zA-Z0-9]+)*(?:\s+(?:dot|\.)\s+[a-zA-Z0-9]+)*)\s+(?:at|@)\s+([a-zA-Z]+(?:\s+[a-zA-Z]+)*(?:\s+(?:dot|\.)\s+[a-zA-Z]+)+)/i);
              if (spokenEmailMatch && spokenEmailMatch[1] && spokenEmailMatch[2]) {
                // IMPORTANT: Convert "james bond" to "jamesbond" - NO DOT between name parts
                // (unless user explicitly says "james dot bond")
                let localPart = spokenEmailMatch[1]
                  .replace(/\s+(?:dot|\.)\s+/gi, ".")
                  .replace(/\s+/g, "")
                  .toLowerCase();
                // Convert "g mail dot com" or "gmail dot com" to "gmail.com"
                let domainPart = spokenEmailMatch[2]
                  .replace(/\s+(?:dot|\.)\s+/gi, ".")
                  .replace(/\s+/g, "")
                  .toLowerCase();
                if (!domainPart.includes(".")) domainPart = `${domainPart}.com`;
                const email = `${localPart}@${domainPart}`;
                if (isValidEmail(email)) {
                  console.log(`✅ Extracted email from spoken format (${isPartial ? 'partial' : 'final'}):`, email);
                  newData.email = email;
                  // Update immediately for real-time modal update
                  setCollectedData((prev) => ({ ...prev, email }));
                }
              }
            }
            
            // Extract phone from confirmation patterns - handle spoken numbers
            const phoneMatch = transcript.match(/(?:your\s+phone\s+(?:number\s+)?(?:as|is)|phone\s+(?:number\s+)?(?:as|is)|it's|it\s+is)\s+([\d]{3}[-.\s]?[\d]{3}[-.\s]?[\d]{4})/i);
            if (phoneMatch && phoneMatch[1]) {
              let phone = phoneMatch[1].replace(/[\s]/g, "");
              if (phone.length === 10) {
                phone = `${phone.slice(0, 3)}-${phone.slice(3, 6)}-${phone.slice(6)}`;
                console.log(`✅ Extracted phone from assistant confirmation (${isPartial ? 'partial' : 'final'}):`, phone);
                newData.phone = phone;
                // Update immediately for real-time modal update
                setCollectedData((prev) => ({ ...prev, phone }));
              }
            }
            
            // Extract phone from spoken format "four one eight five seven five five one seven seven"
            const spokenPhoneMatch = transcript.match(/(?:your\s+phone\s+(?:number\s+)?(?:as|is)|phone\s+(?:number\s+)?(?:as|is)|it's|it\s+is)\s+((?:four|one|eight|five|seven|two|three|six|nine|zero|\d)(?:\s+(?:four|one|eight|five|seven|two|three|six|nine|zero|\d|\s|-))*)/i);
            if (spokenPhoneMatch && spokenPhoneMatch[1]) {
              const numberMap: Record<string, string> = {
                "zero": "0", "one": "1", "two": "2", "three": "3", "four": "4",
                "five": "5", "six": "6", "seven": "7", "eight": "8", "nine": "9"
              };
              let phoneStr = spokenPhoneMatch[1]
                .replace(/\b(zero|one|two|three|four|five|six|seven|eight|nine)\b/gi, (m) => numberMap[m.toLowerCase()] || m)
                .replace(/\s+/g, "")
                .replace(/[^\d-]/g, "");
              
              if (phoneStr.length === 10) {
                const formatted = `${phoneStr.slice(0, 3)}-${phoneStr.slice(3, 6)}-${phoneStr.slice(6)}`;
                console.log(`✅ Extracted phone from spoken format (${isPartial ? 'partial' : 'final'}):`, formatted);
                newData.phone = formatted;
                // Update immediately for real-time modal update
                setCollectedData((prev) => ({ ...prev, phone: formatted }));
              } else if (phoneStr.length >= 10 && phoneStr.length <= 15) {
                console.log(`✅ Extracted phone from spoken format (${isPartial ? 'partial' : 'final'}):`, phoneStr);
                newData.phone = phoneStr;
                // Update immediately for real-time modal update
                setCollectedData((prev) => ({ ...prev, phone: phoneStr }));
              }
            }
            
            // Extract name from confirmation patterns
            const nameMatch = transcript.match(/(?:your\s+name\s+(?:as|is)|name\s+(?:as|is)|full\s+name\s+(?:as|is)|it's|it\s+is)\s+([A-Z][a-z]+(?:\s+[A-Z][a-z]+)+)/i);
            if (nameMatch && nameMatch[1]) {
              const name = nameMatch[1].trim();
              if (name.length >= 2 && name.length <= 50) {
                console.log(`✅ Extracted name from assistant confirmation (${isPartial ? 'partial' : 'final'}):`, name);
                newData.username = name;
                // Update immediately for real-time modal update
                setCollectedData((prev) => ({ ...prev, username: name }));
              }
            }
          }
          
          // For final assistant transcripts, also use GPT-4 for accuracy (refinement)
          if (isFinal) {
            extractDataWithGPT4(transcript, "assistant", collectedData).then((gptData) => {
              if (Object.keys(gptData).length > 0) {
                setCollectedData((prev) => {
                  const updated = { ...prev };
                  if (gptData.email && isValidEmail(gptData.email)) {
                    console.log("✅ Refined email from assistant final transcript (GPT-4):", gptData.email);
                    updated.email = gptData.email;
                  }
                  if (gptData.username && gptData.username.length >= 2) {
                    console.log("✅ Refined username from assistant final transcript (GPT-4):", gptData.username);
                    updated.username = gptData.username;
                  }
                  if (gptData.phone && isValidPhone(gptData.phone)) {
                    console.log("✅ Refined phone from assistant final transcript (GPT-4):", gptData.phone);
                    updated.phone = gptData.phone;
                  }
                  return updated;
                });
              }
            }).catch((error) => {
              console.error("❌ Error refining data with GPT-4:", error);
            });
          }
          
          // Check for confirmation phrases or data confirmation patterns - check BOTH partial and final
          // This allows modal to show as soon as AI starts confirming
          if (isPartial || isFinal) {
            if (checkForConfirmationPhrase(transcript)) {
              console.log(`✅ VAPI mentioned confirmation in ${isPartial ? 'partial' : 'final'} assistant transcript`);
              confirmationDetectedRef.current = true;
              
              // If AI says "let me confirm" or similar, show modal immediately if we have any data
              if (Object.keys(collectedData).length > 0 || newData.email || newData.phone || newData.username) {
                console.log("📋 Confirmation phrase detected - triggering modal display immediately");
                setTimeout(() => {
                  setShowConfirmation(true);
                }, 300);
              }
            }
            
            // Also check if AI is confirming back the data (like "your email as X" or "it's X")
            if (checkForDataConfirmation(transcript) || /(?:it's|it\s+is).*?(?:email|phone|name)/i.test(transcript)) {
              console.log(`✅ VAPI is confirming data back to user in ${isPartial ? 'partial' : 'final'} transcript`);
              confirmationDetectedRef.current = true;
              
              // CRITICAL: Extract corrected data from AI confirmation immediately
              // This handles cases where user says "no, change my email to X" and AI confirms "I've updated your email to X"
              const isCorrectionConfirmation = /(?:updated|changed|corrected|fixed|changed to|updated to|now|is now)/i.test(transcript);
              if (isCorrectionConfirmation && transcript.length > 30 && isFinal) {
                console.log("🔄 AI is confirming a correction - extracting updated data immediately");
                // Use GPT-4 to extract the corrected data (only from final transcripts for corrections to avoid API spam)
                extractDataWithGPT4(transcript, "assistant", collectedData).then((gptData) => {
                  if (Object.keys(gptData).length > 0) {
                    setCollectedData((prev) => {
                      const updated = { ...prev };
                      // ALWAYS update from correction confirmations
                      if (gptData.email && isValidEmail(gptData.email)) {
                        console.log("✅ Updated email from correction confirmation:", gptData.email);
                        updated.email = gptData.email;
                      }
                      if (gptData.username && gptData.username.length >= 2) {
                        console.log("✅ Updated username from correction confirmation:", gptData.username);
                        updated.username = gptData.username;
                      }
                      if (gptData.phone && isValidPhone(gptData.phone)) {
                        console.log("✅ Updated phone from correction confirmation:", gptData.phone);
                        updated.phone = gptData.phone;
                      }
                      console.log("📦 Updated data after correction confirmation:", updated);
                      return updated;
                    });
                  }
                }).catch((error) => {
                  console.error("❌ Error extracting correction with GPT-4:", error);
                });
              }
              
              // If we have data from this confirmation, show modal immediately
              if (newData.email || newData.phone || newData.username || isDataComplete) {
                console.log("📋 Data confirmation detected - triggering modal display");
                setTimeout(() => {
                  setShowConfirmation(true);
                }, 300);
              }
            }
          }
        }
        
        
        // USER transcripts - DO NOT extract from user speech
        // The dialog should ONLY show what VAPI (assistant) confirms
        // Extracting from user speech causes data to change incorrectly
        if (isUser) {
          // Only log for debugging, but DO NOT update collectedData
          console.log(`📝 User transcript (${isPartial ? 'partial' : 'final'}):`, transcript.substring(0, 100));
          // We intentionally do NOT extract from user transcripts
          // The correct data will come from VAPI's confirmation
        }

        // DO NOT extract name from user transcripts
        // Dialog should ONLY show what VAPI (assistant) confirms
        // Name extraction from user speech causes it to constantly change incorrectly
      }


      // Handle conversation-update events (full conversation)
      // Use GPT-4 to extract data from the full conversation context
      if (event.type === "conversation-update" && event.conversation) {
        console.log("💬 Processing conversation update with GPT-4");
        
        // Extract user messages only
        const userMessages = event.conversation
          .filter((msg: any) => msg.role === "user" && msg.content)
          .map((msg: any) => msg.content)
          .join(" ");
        
        // Extract assistant messages for confirmation detection
        const assistantMessages = event.conversation
          .filter((msg: any) => msg.role === "assistant" && msg.content)
          .map((msg: any) => msg.content)
          .join(" ");
        
        // Check if assistant mentioned confirmation or is confirming data
        if (assistantMessages) {
          if (checkForConfirmationPhrase(assistantMessages)) {
            console.log("✅ VAPI mentioned confirmation in conversation");
            confirmationDetectedRef.current = true;
          }
          // Also check if AI is confirming back the data
          if (checkForDataConfirmation(assistantMessages)) {
            console.log("✅ VAPI is confirming data back to user in conversation");
            confirmationDetectedRef.current = true;
            
            // CRITICAL: Extract corrected data from AI confirmation immediately
            const isCorrectionConfirmation = /(?:updated|changed|corrected|fixed|changed to|updated to|now|is now)/i.test(assistantMessages);
            if (isCorrectionConfirmation) {
              console.log("🔄 AI is confirming a correction in conversation - extracting updated data immediately");
              // Use GPT-4 to extract the corrected data
              extractDataWithGPT4(assistantMessages, "assistant", collectedData).then((gptData) => {
                if (Object.keys(gptData).length > 0) {
                  setCollectedData((prev) => {
                    const updated = { ...prev };
                    // ALWAYS update from correction confirmations
                    if (gptData.email && isValidEmail(gptData.email)) {
                      console.log("✅ Updated email from correction confirmation (conversation):", gptData.email);
                      updated.email = gptData.email;
                    }
                    if (gptData.username && gptData.username.length >= 2) {
                      console.log("✅ Updated username from correction confirmation (conversation):", gptData.username);
                      updated.username = gptData.username;
                    }
                    if (gptData.phone && isValidPhone(gptData.phone)) {
                      console.log("✅ Updated phone from correction confirmation (conversation):", gptData.phone);
                      updated.phone = gptData.phone;
                    }
                    console.log("📦 Updated data after correction confirmation (conversation):", updated);
                    return updated;
                  });
                }
              });
            }
            
            // If we have data, show modal immediately
            if (isDataComplete && Object.keys(collectedData).length > 0) {
              console.log("📋 Data confirmed in conversation - triggering modal display");
              setTimeout(() => {
                setShowConfirmation(true);
              }, 1000);
            }
          }
        }
        
        // DO NOT extract from user messages - ONLY extract from VAPI assistant confirmations
        // Extracting from user messages causes wrong data to overwrite VAPI's correct confirmations
        console.log("📝 Conversation update: Skipping user message extraction (VAPI confirmations only)");
      }

      // Handle call-end events - extract from full conversation
      if (event.type === "call-end") {
        // Try to extract from any transcript or summary in the event
        const eventString = JSON.stringify(event);
        const extractedEmail = extractEmail(eventString);
        if (extractedEmail && !newData.email) {
          newData.email = extractedEmail;
        }
        const extractedPhone = extractPhone(eventString);
        if (extractedPhone && !newData.phone) {
          newData.phone = extractedPhone;
        }
      }

      // Handle speech-update events
      // DO NOT extract from user speech - ONLY extract from VAPI confirmations
      if (event.type === "speech-update" && event.role === "user" && (event.speech || event.text)) {
        const speechText = typeof event.speech === "string" 
          ? event.speech 
          : typeof event.text === "string"
          ? event.text
          : JSON.stringify(event.speech || event.text);

        // Log for debugging only - DO NOT extract or update data
        console.log("📝 User speech-update (not extracting):", speechText.substring(0, 100));
        // Data will come from VAPI's confirmations, not from user speech
      }

      // Handle voice-input events (assistant's voice input confirmation)
      // This is when VAPI is speaking/confirming - IMPORTANT: Extract data from confirmations
      if (event.type === "voice-input" && event.input) {
        const voiceInputText = typeof event.input === "string" 
          ? event.input 
          : JSON.stringify(event.input);
        
        console.log("🎤 Processing voice-input event (assistant):", voiceInputText);
        
          // Check if assistant mentioned confirmation or is confirming data
          if (checkForConfirmationPhrase(voiceInputText)) {
            console.log("✅ VAPI mentioned confirmation in voice-input");
            confirmationDetectedRef.current = true;
            
            // Show modal immediately when confirmation phrase is detected
            if (Object.keys(collectedData).length > 0) {
              console.log("📋 Confirmation phrase in voice-input - triggering modal display immediately");
              setTimeout(() => {
                setShowConfirmation(true);
              }, 300);
            }
          }
          
          // Also check if AI is confirming back the data
          if (checkForDataConfirmation(voiceInputText)) {
            console.log("✅ VAPI is confirming data back to user in voice-input");
            confirmationDetectedRef.current = true;
            
            // Extract data immediately from voice-input for real-time updates
            const emailMatch = voiceInputText.match(/(?:your\s+email\s+(?:as|is)|email\s+(?:as|is))\s+([a-zA-Z0-9](?:[a-zA-Z0-9._-]*[a-zA-Z0-9])?@[a-zA-Z0-9](?:[a-zA-Z0-9.-]*[a-zA-Z0-9])?\.[a-zA-Z]{2,})/i);
            if (emailMatch && emailMatch[1]) {
              const email = emailMatch[1].toLowerCase().trim();
              if (isValidEmail(email)) {
                console.log("✅ Extracted email from voice-input (real-time):", email);
                newData.email = email;
              }
            }
            
            const phoneMatch = voiceInputText.match(/(?:your\s+phone\s+(?:number\s+)?(?:as|is)|phone\s+(?:number\s+)?(?:as|is))\s+([\d]{3}[-.\s]?[\d]{3}[-.\s]?[\d]{4})/i);
            if (phoneMatch && phoneMatch[1]) {
              let phone = phoneMatch[1].replace(/[\s]/g, "");
              if (phone.length === 10) {
                phone = `${phone.slice(0, 3)}-${phone.slice(3, 6)}-${phone.slice(6)}`;
                console.log("✅ Extracted phone from voice-input (real-time):", phone);
                newData.phone = phone;
              }
            }
            
            const nameMatch = voiceInputText.match(/(?:your\s+name\s+(?:as|is)|name\s+(?:as|is)|full\s+name\s+(?:as|is))\s+([A-Z][a-z]+(?:\s+[A-Z][a-z]+)+)/i);
            if (nameMatch && nameMatch[1]) {
              const name = nameMatch[1].trim();
              if (name.length >= 2 && name.length <= 50) {
                console.log("✅ Extracted name from voice-input (real-time):", name);
                newData.username = name;
              }
            }
            
            // CRITICAL: Extract corrected data from AI confirmation immediately
            const isCorrectionConfirmation = /(?:updated|changed|corrected|fixed|changed to|updated to|now|is now)/i.test(voiceInputText);
            if (isCorrectionConfirmation && voiceInputText.length > 30) {
              console.log("🔄 AI is confirming a correction in voice-input - extracting updated data immediately");
              // Use GPT-4 to extract the corrected data
              extractDataWithGPT4(voiceInputText, "assistant", collectedData).then((gptData) => {
                if (Object.keys(gptData).length > 0) {
                  setCollectedData((prev) => {
                    const updated = { ...prev };
                    // ALWAYS update from correction confirmations
                    if (gptData.email && isValidEmail(gptData.email)) {
                      console.log("✅ Updated email from correction confirmation (voice-input):", gptData.email);
                      updated.email = gptData.email;
                    }
                    if (gptData.username && gptData.username.length >= 2) {
                      console.log("✅ Updated username from correction confirmation (voice-input):", gptData.username);
                      updated.username = gptData.username;
                    }
                    if (gptData.phone && isValidPhone(gptData.phone)) {
                      console.log("✅ Updated phone from correction confirmation (voice-input):", gptData.phone);
                      updated.phone = gptData.phone;
                    }
                    console.log("📦 Updated data after correction confirmation (voice-input):", updated);
                    return updated;
                  });
                }
              });
            }
            
            // If we have data, show modal immediately
            if (newData.email || newData.phone || newData.username || isDataComplete) {
              console.log("📋 Data confirmed in voice-input - triggering modal display");
              setTimeout(() => {
                setShowConfirmation(true);
              }, 300);
            }
          }
        
        // ALWAYS try to extract from voice-input if it contains data patterns
        // Voice-input often contains confirmations like "your email as X" or "phone as Y"
        const hasDataPattern = /(?:email|phone|name)\s+(?:is|as)/i.test(voiceInputText);
        const isCorrection = /(?:no|that's\s+wrong|change|correct|update|actually|it's|can\s+you\s+change)/i.test(voiceInputText);
        
        // Use GPT-4 if it has data patterns OR if it's a correction
        if (hasDataPattern || isCorrection) {
          console.log("🤖 Using GPT-4 to extract from voice-input (hasDataPattern:", hasDataPattern, "isCorrection:", isCorrection, "):", voiceInputText.substring(0, 200));
          extractDataWithGPT4(voiceInputText, "assistant", collectedData).then((gptData) => {
            console.log("🤖 GPT-4 extraction result from voice-input:", gptData);
            if (Object.keys(gptData).length > 0) {
              setCollectedData((prev) => {
                const updated = { ...prev };
                // ALWAYS update from assistant confirmations if data is valid
                // This is especially important for email - always extract and display it
                if (gptData.email && isValidEmail(gptData.email)) {
                  console.log("✅ Updating email from voice-input confirmation:", gptData.email);
                  updated.email = gptData.email;
                } else {
                  console.log("⚠️ Email not extracted from voice-input:", { 
                    hasEmail: !!gptData.email, 
                    isValid: gptData.email ? isValidEmail(gptData.email) : false,
                    email: gptData.email 
                  });
                }
                if (gptData.username && gptData.username.length >= 2) {
                  console.log("✅ Updating username from voice-input confirmation:", gptData.username);
                  updated.username = gptData.username;
                }
                if (gptData.phone && isValidPhone(gptData.phone)) {
                  console.log("✅ Updating phone from voice-input confirmation:", gptData.phone);
                  updated.phone = gptData.phone;
                }
                console.log("📦 Updated collected data after voice-input:", updated);
                
                // If we extracted data and AI is confirming, show modal
                if (Object.keys(updated).length > 0 && (hasDataPattern || isCorrection)) {
                  const hasCompleteData = updated.email || updated.phone || updated.username;
                  if (hasCompleteData) {
                    console.log("📋 Data extracted from voice-input - triggering modal display");
                    setTimeout(() => {
                      setShowConfirmation(true);
                    }, 1500);
                  }
                }
                
                return updated;
              });
            } else {
              console.log("⚠️ No data extracted from voice-input");
            }
          }).catch((error) => {
            console.error("❌ Error extracting from voice-input confirmation with GPT-4:", error);
          });
        }
      }

    // Update collected data if we found anything
    // CRITICAL: Update immediately for real-time modal updates
    if (Object.keys(newData).length > 0) {
      console.log("✅ Extracted new data:", newData);
      setCollectedData((prev) => {
        const updated = { ...prev };
        
        // Always update fields when valid data is extracted (allows user to change by voice)
        // This enables real-time updates in the modal as user speaks OR as AI confirms
        if (newData.email) {
          if (isValidEmail(newData.email)) {
            updated.email = newData.email;
            console.log("📧 Updated email (real-time):", newData.email);
          } else {
            console.warn("⚠️ Invalid email format, skipping:", newData.email);
          }
        }
        
        if (newData.phone) {
          if (isValidPhone(newData.phone)) {
            updated.phone = newData.phone;
            console.log("📞 Updated phone (real-time):", newData.phone);
          } else {
            console.warn("⚠️ Invalid phone format, skipping:", newData.phone);
          }
        }
        
        if (newData.username) {
          // Update username when valid name is extracted (allows changes by voice)
          if (newData.username.length > 2 && /^[A-Z][a-z]+/.test(newData.username)) {
            updated.username = newData.username;
            console.log("👤 Updated username (real-time):", newData.username);
          }
        }
        
        console.log("📦 Updated collected data (real-time):", updated);
        
        // If this is from an assistant confirmation (partial or final) and we have data, show modal quickly
        if (event.role === "assistant" && (updated.email || updated.phone || updated.username)) {
          const hasCompleteData = updated.email || updated.phone || updated.username;
          if (hasCompleteData) {
            // For partial transcripts, show modal faster (as AI is speaking)
            const isPartial = event.type === "transcript" && event.transcriptType === "partial";
            const delay = isPartial ? 300 : 800;
            console.log(`📋 Assistant confirmed data (${isPartial ? 'partial' : 'final'}) - triggering modal display in ${delay}ms`);
            setTimeout(() => {
              setShowConfirmation(true);
            }, delay);
          }
        }
        
        return updated;
      });
    } else {
      console.log("ℹ️ No data extracted from event");
    }
  }, []);

  // Check if we have complete data (at least email or phone)
  const isDataComplete = Boolean(
    collectedData.email || collectedData.phone || collectedData.username
  );

  // Show confirmation modal when VAPI mentions confirmation AND we have data
  useEffect(() => {
    // Check if we should show the modal
    const shouldShow = 
      confirmationDetectedRef.current &&
      isDataComplete &&
      !showConfirmation &&
      !hasSentRef.current &&
      Object.keys(collectedData).length > 0;
    
    if (shouldShow) {
      console.log("✅ Showing confirmation modal - VAPI mentioned confirmation and data is ready", {
        confirmationDetected: confirmationDetectedRef.current,
        isDataComplete,
        showConfirmation,
        hasSent: hasSentRef.current,
        collectedDataKeys: Object.keys(collectedData),
        collectedData
      });
      const timer = setTimeout(() => {
        if (!hasSentRef.current && !showConfirmation) {
          console.log("📋 Opening confirmation modal now");
          setShowConfirmation(true);
        }
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [isDataComplete, showConfirmation, collectedData]);
  
  // Also show modal when we have complete data and AI has confirmed it (more aggressive trigger)
  useEffect(() => {
    // If we have complete data and confirmation was detected, show modal
    const hasCompleteData = collectedData.email || collectedData.phone || collectedData.username;
    const shouldShow = 
      hasCompleteData &&
      confirmationDetectedRef.current &&
      !showConfirmation &&
      !hasSentRef.current;
    
    if (shouldShow) {
      console.log("✅ Showing confirmation modal - data complete and AI confirmed", {
        hasCompleteData,
        confirmationDetected: confirmationDetectedRef.current,
        showConfirmation,
        hasSent: hasSentRef.current,
        collectedData
      });
      const timer = setTimeout(() => {
        if (!hasSentRef.current && !showConfirmation) {
          console.log("📋 Opening confirmation modal (aggressive trigger)");
          setShowConfirmation(true);
        }
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [collectedData, showConfirmation]);

  // Send collected data to team
  const sendDataToTeam = useCallback(async () => {
    console.log("📤 Attempting to send data:", {
      isDataComplete,
      hasSent: hasSentRef.current,
      isSending,
      collectedData,
    });

    if (hasSentRef.current || isSending) {
      console.log("⏭️ Skipping send - already sent or sending");
      return;
    }

    setIsSending(true);
    setSendError(null);
    hasSentRef.current = true;

    const payload = {
      email: collectedData.email,
      username: collectedData.username,
      phone: collectedData.phone,
      collectedAt: new Date().toISOString(),
      additionalData: Object.keys(collectedData)
        .filter((key) => !["email", "username", "phone"].includes(key))
        .reduce((acc, key) => {
          acc[key] = collectedData[key];
          return acc;
        }, {} as Record<string, any>),
    };

    console.log("📤 Sending payload to API:", payload);

    try {
      // Use Supabase Edge Function URL
      const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL || "https://jsqybuexlbjbsncmkxhk.supabase.co";
      const response = await fetch(`${SUPABASE_URL}/functions/v1/send-lead`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      console.log("📥 API Response status:", response.status);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: "Unknown error" }));
        console.error("❌ API Error:", errorData);
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log("✅ Lead data sent successfully:", result);
      // Return success indicator
      return;
    } catch (error: any) {
      console.error("❌ Error sending lead data:", error);
      setSendError(error.message || "Failed to send data");
      hasSentRef.current = false; // Allow retry on error
      throw error; // Re-throw so caller can handle it
    } finally {
      setIsSending(false);
    }
  }, [collectedData, isDataComplete, isSending]);

  // Don't auto-show modal - let the Call component control when to show it
  // The modal will be shown after the call ends, not immediately when data is collected

  // Reset collected data
  const resetData = useCallback(() => {
    setCollectedData({});
    setSendError(null);
    setShowConfirmation(false);
    hasSentRef.current = false;
    confirmationDetectedRef.current = false; // Reset confirmation detection
  }, []);

  // Reset confirmation detection (allows modal to show again)
  const resetConfirmationDetection = useCallback(() => {
    confirmationDetectedRef.current = false;
    setShowConfirmation(false);
    hasSentRef.current = false;
  }, []);

  // Update collected data manually (for user editing)
  const updateCollectedData = useCallback((newData: CollectedUserData) => {
    setCollectedData((prev) => ({
      ...prev,
      ...newData,
    }));
    console.log("✏️ Manually updated collected data:", newData);
  }, []);

  return {
    collectedData,
    isDataComplete,
    sendDataToTeam,
    isSending,
    sendError,
    resetData,
    handleVapiData, // Expose handler for useVapi
    showConfirmation,
    setShowConfirmation,
    resetConfirmationDetection,
    updateCollectedData, // For manual editing
  };
}

